import { Capacitor } from '@capacitor/core';

/**
 * Detección de plataforma + base URL del backend. Es la ÚNICA frontera entre el
 * comportamiento web (same-origin + cookie httpOnly) y el nativo (base absoluta
 * + Bearer token) de la app empaquetada con Capacitor.
 *
 * Fase 23 — ver `docs/speak-plan/23-mobile-capacitor.md` §3.1. En `@capacitor/core`
 * `isNativePlatform()` devuelve `false` en web (incluida la PWA instalada), así
 * que importar este módulo en el bundle web es inofensivo.
 */

/** `true` sólo dentro del wrapper nativo de Capacitor (iOS/Android); `false` en web. */
export const isNative: boolean = Capacitor.isNativePlatform();

/**
 * Plataforma concreta del runtime: `'ios' | 'android'` en el wrapper nativo,
 * `'web'` fuera de él. Se usa para elegir recursos por-plataforma (p. ej. la
 * public SDK key de RevenueCat, que es distinta en iOS y en Android).
 */
export function nativePlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

/** `true` only inside the iOS wrapper (iPhone/iPad); `false` on Android and on the web. */
export function isIOS(): boolean {
  return isNative && nativePlatform() === 'ios';
}

/**
 * The JS handle of a native plugin, read from the bridge's registry
 * (`Capacitor.Plugins`, which the native side fills with the plugins compiled into
 * the binary) WITHOUT importing the plugin's npm package. `null` on the web and on a
 * binary that does not have the plugin — so the caller feature-detects for free.
 *
 * Why not the package: for `@capgo/capacitor-updater` (`lib/native/ota.ts`) importing
 * it also runs its `history.js`, which patches `history.pushState` for
 * `keepUrlPathAfterReload` — behaviour Club Arena (whose controller this is) never
 * shipped nor measured on devices. The registry handle's `addListener` may return the
 * handle itself instead of a Promise; await it or wrap it in `Promise.resolve`.
 */
export function getPlugin<T>(name: string): T | null {
  if (!isNative) return null;
  const registry = (Capacitor as unknown as { Plugins?: Record<string, unknown> }).Plugins;
  return (registry?.[name] as T | undefined) ?? null;
}

/**
 * Base URL del backend para los requests del cliente.
 *
 * - **Web**: `''` → rutas relativas same-origin, como siempre (cero regresión).
 * - **Nativo**: `VITE_NATIVE_API_URL`, horneada al build (apunta a prod o
 *   staging según el build). Es **obligatoria**: si falta, lanzamos (fail-fast,
 *   regla dura del repo) en vez de degradar a un default silencioso — un origin
 *   mal resuelto en nativo mandaría todos los requests a la nada sin avisar.
 */
export function apiBaseUrl(): string {
  if (!isNative) return '';
  const url = import.meta.env.VITE_NATIVE_API_URL as string | undefined;
  if (!url) {
    throw new Error(
      'VITE_NATIVE_API_URL no está configurada para el build nativo de speak: el ' +
        'cliente nativo necesita la base absoluta del backend (ver ' +
        'docs/speak-plan/23-mobile-capacitor.md §3.1).',
    );
  }
  return url;
}

/**
 * Base URL del WebSocket de voz, derivada de `apiBaseUrl()` (https→wss, http→ws).
 * En web devuelve `''` y el cliente cae al default same-origin de `buildSessionUrl`.
 * En nativo el call-site de voz arma `${apiWsBaseUrl()}/ws/session?token=<jwt>`
 * (Fase 23 §3.3) — `buildSessionUrl` respeta `opts.url` sin tocar el protocolo.
 */
export function apiWsBaseUrl(): string {
  const base = apiBaseUrl();
  if (!base) return '';
  return base.replace(/^http/, 'ws');
}
